/**
 * MigratingTab — Self-contained tab for migrating legacy 2.0 data to 4.0.
 *
 * Uses a pp-subtab-toggle segmented control to switch between migration
 * categories. Each category shows a single pp-section card with a checkbox
 * table and migrate action.
 *
 * Categories: Groups, Users, AFR Devices, HP Alerts, Reason Codes, Alert Settings
 *
 * Props:
 *   campusId     — current campus ID (number)
 *   campusGuid   — campus GUID for DynamoDB partition key
 *   editRights   — boolean (can migrate)
 *   showToast    — (type, message) => void
 *   getSessionParams — () => { campusId, userId, clientId }
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  migrateUsers, migrateGroups, migrateAFRDevices,
  migrateHPAlerts, migrateReasonCodes,
} from '../api/campusProfileApi.js';
import {
  loadMigrationSources, availableUsers, availableGroups, availableAFR, availableHP,
  availableReasons, alertSettingsAvailable as alertsAvailable, profileSession,
  buildUsersPayload, buildGroupsPayload, buildAfrPayloads, buildHpPayloads, buildReasonsPayload,
  migrateAlertAndCustomSettings,
} from '../utils/cloudMigration.js';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Sort comparator */
const sortBy = (arr, key, order = 'asc') => {
  return [...arr].sort((a, b) => {
    const aVal = (a[key] || '').toString().toLowerCase();
    const bVal = (b[key] || '').toString().toLowerCase();
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });
};

/** Checkbox table for selectable items */
const CheckboxTable = ({ columns, data, idKey, selected, onToggle, onToggleAll }) => {
  const allSelected = data.length > 0 && selected.length === data.length;

  return (
    <div className="dp-table-wrap" style={{ minWidth: '400px' }}>
      <table className="dp-table">
        <thead className="dp-thead">
          <tr>
            <th className="dp-th" style={{ width: '40px' }}>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => onToggleAll()}
              />
            </th>
            {columns.map((col) => (
              <th key={col.key} className="dp-th">{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => {
            const id = row[idKey] ?? i;
            const isChecked = selected.includes(id);
            return (
              <tr key={id} className="dp-tr">
                <td className="dp-td" style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => onToggle(id)}
                  />
                </td>
                {columns.map((col) => (
                  <td key={col.key} className="dp-td">{row[col.key] ?? '\u2014'}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Component ────────────────────────────────────────────────────────────────

const MigratingTab = ({ campusId, campusGuid, editRights, showToast, getSessionParams }) => {
  // ── Data State ────────────────────────────────────────────────────────────
  // DDB (2.0)
  const [ddbUsers, setDdbUsers] = useState([]);
  const [ddbGroups, setDdbGroups] = useState([]);
  const [ddbAFR, setDdbAFR] = useState([]);
  const [ddbHP, setDdbHP] = useState([]);
  const [ddbAlerts, setDdbAlerts] = useState([]);
  const [ddbReasonCodes, setDdbReasonCodes] = useState([]);

  // 4.0 (SQL)
  const [myUsers, setMyUsers] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [myAFR, setMyAFR] = useState([]);
  const [myHP, setMyHP] = useState([]);
  const [myReasonCodes, setMyReasonCodes] = useState([]);
  const [myAlerts, setMyAlerts] = useState([]);

  // Lookup tables
  const [eventTypes, setEventTypes] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);

  // Loading / migrating
  const [loaded, setLoaded] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // Sub-tab + selection state
  const [subTab, setSubTab] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedAFR, setSelectedAFR] = useState([]);
  const [selectedHP, setSelectedHP] = useState([]);
  const [selectedReasons, setSelectedReasons] = useState([]);

  // ── Data Loading ──────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!campusGuid) {
      setLoaded(true);
      return;
    }

    const src = await loadMigrationSources(campusId, campusGuid);
    setDdbUsers(src.ddbUsers);
    setDdbGroups(src.ddbGroups);
    setDdbAFR(src.ddbAFR);
    setDdbHP(src.ddbHP);
    setDdbAlerts(src.ddbAlerts);
    setDdbReasonCodes(src.ddbReasonCodes);
    setMyUsers(src.myUsers);
    setMyGroups(src.myGroups);
    setMyAFR(src.myAFR);
    setMyHP(src.myHP);
    setMyReasonCodes(src.myReasonCodes);
    setMyAlerts(src.myAlerts);
    setEventTypes(src.eventTypes);
    setDeviceTypes(src.deviceTypes);
    setLoaded(true);
  }, [campusId, campusGuid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Computed datasets (2.0 items not yet in 4.0) ──────────────────────────

  /* The comparisons live in utils/cloudMigration.js, shared with turning on "Upgraded to 5.0". */
  const activeUsers = useMemo(() => availableUsers(ddbUsers, myUsers), [ddbUsers, myUsers]);
  const activeGroups = useMemo(() => availableGroups(ddbGroups, myGroups), [ddbGroups, myGroups]);
  const [afrDataSet, afrOriginIds] = useMemo(() => availableAFR(ddbAFR, myAFR, deviceTypes), [ddbAFR, myAFR, deviceTypes]);
  const [hpDataSet, hpOriginIds] = useMemo(() => availableHP(ddbHP, myHP, eventTypes), [ddbHP, myHP, eventTypes]);
  const [reasonDataSet] = useMemo(() => availableReasons(ddbReasonCodes, myReasonCodes), [ddbReasonCodes, myReasonCodes]);
  const alertSettingsAvailable = useMemo(() => alertsAvailable(ddbAlerts, myAlerts), [ddbAlerts, myAlerts]);

  const hasAnything = activeUsers.length > 0 || activeGroups.length > 0 || afrDataSet.length > 0 ||
    hpDataSet.length > 0 || reasonDataSet.length > 0 || alertSettingsAvailable;

  // ── Available sub-tab categories ────────────────────────────────────────
  const categories = useMemo(() => {
    const list = [];
    if (activeGroups.length > 0)   list.push({ key: 'groups',  label: 'Groups',    count: activeGroups.length });
    if (activeUsers.length > 0)    list.push({ key: 'users',   label: 'Users',     count: activeUsers.length });
    if (afrDataSet.length > 0)     list.push({ key: 'afr',     label: 'AFR',       count: afrDataSet.length });
    if (hpDataSet.length > 0)      list.push({ key: 'hp',      label: 'HP Alerts', count: hpDataSet.length });
    if (reasonDataSet.length > 0)  list.push({ key: 'reasons', label: 'Reasons',   count: reasonDataSet.length });
    if (alertSettingsAvailable)    list.push({ key: 'alerts',  label: 'Settings',  count: 1 });
    return list;
  }, [activeGroups, activeUsers, afrDataSet, hpDataSet, reasonDataSet, alertSettingsAvailable]);

  // Auto-select first available category when data loads
  useEffect(() => {
    if (loaded && categories.length > 0 && (!subTab || !categories.find((c) => c.key === subTab))) {
      setSubTab(categories[0].key);
    }
  }, [loaded, categories, subTab]);

  // Reset confirming state when switching sub-tabs
  const handleSubTabChange = useCallback((key) => {
    setSubTab(key);
    setConfirming(false);
  }, []);

  // ── Toggle helpers ────────────────────────────────────────────────────────
  const toggle = (setter, id) => {
    setter((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAll = (setter, data, idKey) => {
    setter((prev) => prev.length === data.length ? [] : data.map((d) => d[idKey]));
  };

  // ── Migration handlers ────────────────────────────────────────────────────

  /** Migrate selected users */
  const handleMigrateUsers = useCallback(async () => {
    setMigrating(true);
    const session = getSessionParams();
    const payload = buildUsersPayload(
      activeUsers.filter((user) => selectedUsers.includes(user.UserId)),
      myGroups,
      { campusId, campusGuid, clientId: session.clientId },
    );

    const result = await migrateUsers(payload, session.userId);
    setMigrating(false);
    setConfirming(false);

    if (result && result[0]) {
      showToast('success', 'All selected users migrated.');
      setSelectedUsers([]);
      loadData();
    } else {
      showToast('error', 'Error during user migration. Check user table.');
    }
  }, [activeUsers, selectedUsers, myGroups, campusId, campusGuid, getSessionParams, showToast, loadData]);

  /** Migrate selected groups */
  const handleMigrateGroups = useCallback(async () => {
    setMigrating(true);
    const session = getSessionParams();
    const payload = buildGroupsPayload(activeGroups.filter((group) => selectedGroups.includes(group.GroupId)), campusId);

    const result = await migrateGroups(payload, profileSession(session, campusId));
    setMigrating(false);
    setConfirming(false);

    if (result && result[0]) {
      showToast('success', 'Groups migrated successfully.');
      setSelectedGroups([]);
      loadData();
    } else {
      showToast('error', 'Error during group migration.');
    }
  }, [activeGroups, selectedGroups, campusId, getSessionParams, showToast, loadData]);

  /** Migrate selected AFR devices */
  const handleMigrateAFR = useCallback(async () => {
    setMigrating(true);
    const session = getSessionParams();

    const { payload, oldPayload } = buildAfrPayloads(selectedAFR, afrOriginIds, campusId);
    // afrSave writes to the request's CampusId: the profile's campus, not the signed-in user's.
    const result = await migrateAFRDevices(payload, oldPayload, profileSession(session, campusId));
    setMigrating(false);
    setConfirming(false);

    if (result && result[0]) {
      showToast('success', 'AFR devices migrated successfully.');
      setSelectedAFR([]);
      loadData();
    } else {
      showToast('error', 'Error during AFR migration.');
    }
  }, [selectedAFR, afrOriginIds, campusId, getSessionParams, showToast, loadData]);

  /** Migrate selected HP alert types */
  const handleMigrateHP = useCallback(async () => {
    setMigrating(true);
    const session = getSessionParams();

    const { payload, oldPayload } = buildHpPayloads(selectedHP, hpOriginIds, campusId);
    // prioritySave writes to the request's CampusId too.
    const result = await migrateHPAlerts(payload, oldPayload, profileSession(session, campusId));
    setMigrating(false);
    setConfirming(false);

    if (result && result[0]) {
      showToast('success', 'High priority alert types migrated successfully.');
      setSelectedHP([]);
      loadData();
    } else {
      showToast('error', 'Error during HP alert migration.');
    }
  }, [selectedHP, hpOriginIds, campusId, getSessionParams, showToast, loadData]);

  /** Migrate selected reason codes */
  const handleMigrateReasons = useCallback(async () => {
    setMigrating(true);
    const session = getSessionParams();

    const payload = buildReasonsPayload(reasonDataSet.filter((r) => selectedReasons.includes(r.reason_code_id)), campusId);

    const result = await migrateReasonCodes(payload, session);
    setMigrating(false);
    setConfirming(false);

    if (result && result[0]) {
      showToast('success', 'Reason codes migrated successfully.');
      setSelectedReasons([]);
      loadData();
    } else {
      showToast('error', 'Error during reason code migration.');
    }
  }, [reasonDataSet, selectedReasons, campusId, getSessionParams, showToast, loadData]);

  /** Migrate alert & custom settings */
  const handleMigrateAlerts = useCallback(async () => {
    setMigrating(true);
    const session = getSessionParams();
    const [ok] = await migrateAlertAndCustomSettings(ddbAlerts[0], campusId, session);

    setMigrating(false);
    setConfirming(false);

    if (ok) {
      showToast('success', 'Alert & customization settings migrated successfully.');
      loadData();
    } else {
      showToast('error', 'Error during alert/custom settings migration.');
    }
  }, [ddbAlerts, campusId, getSessionParams, showToast, loadData]);

  // ── Per-category config lookup ─────────────────────────────────────────

  /** Get the active selection count, handler, and confirm text for the current sub-tab */
  const getSubTabConfig = () => {
    switch (subTab) {
      case 'groups':  return { selected: selectedGroups.length,  onMigrate: handleMigrateGroups,  confirm: 'Groups will be migrated to 4.0. This cannot be undone.' };
      case 'users':   return { selected: selectedUsers.length,   onMigrate: handleMigrateUsers,   confirm: 'Users will be migrated to 4.0. This cannot be undone.' };
      case 'afr':     return { selected: selectedAFR.length,     onMigrate: handleMigrateAFR,     confirm: 'AFR device types will be migrated to 4.0. This cannot be undone.' };
      case 'hp':      return { selected: selectedHP.length,      onMigrate: handleMigrateHP,      confirm: 'High priority event types will be migrated to 4.0. This cannot be undone.' };
      case 'reasons': return { selected: selectedReasons.length, onMigrate: handleMigrateReasons, confirm: 'Reason codes will be migrated to 4.0. This cannot be undone.' };
      case 'alerts':  return { selected: 1,                      onMigrate: handleMigrateAlerts,  confirm: 'This will overwrite any current alert/custom settings.' };
      default:        return { selected: 0, onMigrate: null, confirm: '' };
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (!loaded) {
    return <p style={{ padding: '1rem', color: 'var(--color-text-tertiary, #9ca3af)' }}>Loading migration data...</p>;
  }

  if (!campusGuid) {
    return (
      <div className="cpp-int-empty">
        <p className="cpp-int-empty__title">No campus GUID available</p>
        <p className="cpp-int-empty__subtitle">Migration requires a legacy 2.0 campus GUID.</p>
      </div>
    );
  }

  if (!hasAnything) {
    return (
      <div className="cpp-int-empty">
        <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="var(--color-text-tertiary, #9ca3af)" className="cpp-int-empty__icon" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        <p className="cpp-int-empty__title">No assets to migrate</p>
        <p className="cpp-int-empty__subtitle">All legacy 2.0 data has already been migrated to 4.0.</p>
      </div>
    );
  }

  const sortedUsers = sortBy(activeUsers, 'UserName');
  const sortedGroups = sortBy(activeGroups, 'GroupName');
  const sortedAFR = sortBy(afrDataSet, 'device_type_name');
  const sortedHP = sortBy(hpDataSet, 'event_type_name');
  const sortedReasons = sortBy(reasonDataSet, 'reason_code_description');

  const cfg = getSubTabConfig();
  const activeCat = categories.find((c) => c.key === subTab);
  const sectionTitle = activeCat
    ? `2.0 ${activeCat.label}${activeCat.count != null ? ` (${activeCat.count})` : ''}`
    : '';

  return (
    <>
      {/* Segmented control — category picker */}
      <div className="pp-subtab-toggle" style={{ flexWrap: 'wrap' }}>
        {categories.map((cat) => (
          <button
            key={cat.key}
            className={`pp-subtab-btn${subTab === cat.key ? ' is-active' : ''}`}
            onClick={() => handleSubTabChange(cat.key)}
          >
            {cat.label}
            {cat.count != null && (
              <span className="cpp-mig-badge">{cat.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Active category content — single pp-section card */}
      <div className="pp-section">
        <div className="pp-section__header">
          <h3 className="pp-section__title">{sectionTitle}</h3>
          <div className="pp-section__actions">
            {editRights && cfg.onMigrate && (
              <>
                {confirming ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-danger, #c53030)' }}>
                      {cfg.confirm}
                    </span>
                    <button
                      type="button"
                      className="dp-btn dp-btn--cancel dp-btn--sm"
                      onClick={() => setConfirming(false)}
                      disabled={migrating}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="dp-btn dp-btn--primary dp-btn--sm"
                      onClick={() => cfg.onMigrate()}
                      disabled={migrating}
                    >
                      {migrating ? 'Migrating...' : 'Confirm'}
                    </button>
                  </div>
                ) : (
                  cfg.selected > 0 && (
                    <button
                      type="button"
                      className="dp-btn dp-btn--primary dp-btn--sm"
                      onClick={() => setConfirming(true)}
                      disabled={migrating}
                    >
                      <svg className="dp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
                      </svg>
                      Migrate ({cfg.selected})
                    </button>
                  )
                )}
              </>
            )}
          </div>
        </div>

        <div className="pp-section__body" style={{ padding: '1rem 1.25rem', maxHeight: '500px', overflowY: 'auto' }}>
          {/* Groups */}
          {subTab === 'groups' && (
            <CheckboxTable
              columns={[
                { key: 'GroupName', label: 'Group Name' },
                { key: 'CapCode', label: 'Cap Code' },
              ]}
              data={sortedGroups}
              idKey="GroupId"
              selected={selectedGroups}
              onToggle={(id) => toggle(setSelectedGroups, id)}
              onToggleAll={() => toggleAll(setSelectedGroups, activeGroups, 'GroupId')}
            />
          )}

          {/* Users */}
          {subTab === 'users' && (
            <CheckboxTable
              columns={[
                { key: 'UserName', label: 'Username' },
                { key: 'DisplayName', label: 'Display Name' },
                { key: 'UserRole', label: 'Role' },
              ]}
              data={sortedUsers}
              idKey="UserId"
              selected={selectedUsers}
              onToggle={(id) => toggle(setSelectedUsers, id)}
              onToggleAll={() => toggleAll(setSelectedUsers, activeUsers, 'UserId')}
            />
          )}

          {/* AFR Devices */}
          {subTab === 'afr' && (
            <CheckboxTable
              columns={[
                { key: 'device_type_name', label: 'Device Type' },
              ]}
              data={sortedAFR}
              idKey="device_type_id"
              selected={selectedAFR}
              onToggle={(id) => toggle(setSelectedAFR, id)}
              onToggleAll={() => toggleAll(setSelectedAFR, afrDataSet, 'device_type_id')}
            />
          )}

          {/* High Priority Alert Types */}
          {subTab === 'hp' && (
            <CheckboxTable
              columns={[
                { key: 'event_type_name', label: 'Event Type' },
              ]}
              data={sortedHP}
              idKey="event_type_id"
              selected={selectedHP}
              onToggle={(id) => toggle(setSelectedHP, id)}
              onToggleAll={() => toggleAll(setSelectedHP, hpDataSet, 'event_type_id')}
            />
          )}

          {/* Reason Codes */}
          {subTab === 'reasons' && (
            <CheckboxTable
              columns={[
                { key: 'reason_code_description', label: 'Description' },
              ]}
              data={sortedReasons}
              idKey="reason_code_id"
              selected={selectedReasons}
              onToggle={(id) => toggle(setSelectedReasons, id)}
              onToggleAll={() => toggleAll(setSelectedReasons, reasonDataSet, 'reason_code_id')}
            />
          )}

          {/* Alert & Custom Settings */}
          {subTab === 'alerts' && (
            <div style={{ fontSize: 'var(--font-size-sm, 0.875rem)', lineHeight: 1.8 }}>
              {ddbAlerts.map((a, i) => (
                <ul key={i} style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  <li>Accept: <strong>{String(a.AcceptButtonEnabled).toUpperCase()}</strong></li>
                  <li>Finish: <strong>{String(a.FinishButtonEnabled).toUpperCase()}</strong></li>
                  <li>Reason: <strong>{String(a.ReasonCodeEnabled).toUpperCase()}</strong></li>
                  <li>Release: After <strong>{a.ReleaseTimeInterval}</strong> Minutes</li>
                  <li>Clear: After <strong>{a.ClearTimeInterval}</strong> Minutes</li>
                  <li>Finish: After <strong>{a.FinishTimeInterval}</strong> Minutes</li>
                  <li>Group Edit: <strong>{String(a.CaregiverChgGroupEnabled).toUpperCase()}</strong></li>
                  <li>Display Edit: <strong>{String(a.UserEditDisplayName).toUpperCase()}</strong></li>
                </ul>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MigratingTab;
